import { useForm } from 'react-hook-form';
import {
  HubSpotContact,
  HubSpotCompany,
  BillingInformationModel,
} from '../../../api/billing-types';
import BillingAddressForm from './billing-address-form';
import ContactInfoForm from './contact-info-form';
import ShippingAddressForm from './shipping-address-form';
import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BillingKeys, updateHubSpotContact } from '../../../api/billing-api';
import { ModuleList } from '../../../shared/analytics/module-list';
import { handleErrors } from '../../../utils/errors';
import {
  isAbleToSeeUIElement,
  BillingRoles,
} from '../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import DeletePaymentContactDialog from './delete-payment-contact-dialog';
import { Messages } from '../../../utils/message-constants';
import UserAvatar from '../../../shared/user-avatar';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import AccordionDetails from '@mui/material/AccordionDetails';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import AccordionActions from '@mui/material/AccordionActions';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Delete from '@mui/icons-material/Delete';
import Save from '@mui/icons-material/Save';
interface HubspotContactsFormProps {
  contact: HubSpotContact;
  hubSpotCompany: HubSpotCompany;
  changeLogRoles: string[];
  expanded: string | false;
  setExpanded: (expanded: string | false) => void;
  billingRoles: string[];
  hubSpotContacts: HubSpotContact[];
  billingInformationModel: BillingInformationModel;
}

export default function HubspotContactsForm({
  contact,
  hubSpotCompany,
  changeLogRoles,
  expanded,
  setExpanded,
  billingRoles,
  hubSpotContacts,
  billingInformationModel,
}: HubspotContactsFormProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const defaultMatchBillingAddress =
    contact.properties.street_address_1__shipping_ ===
      contact.properties.address &&
    contact.properties.street_address_2__shipping_ ===
      contact.properties.street_address_2 &&
    contact.properties.city__shipping_ === contact.properties.city &&
    contact.properties.state__shipping_ === contact.properties.state &&
    contact.properties.postal_code__shipping_ === contact.properties.zip &&
    contact.properties.country__shipping_ === contact.properties.country;

  const [matchBillingAddress, setMatchBillingAddress] = useState(
    defaultMatchBillingAddress
  );

  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
    watch,
    setValue,
    getValues,
    trigger,
  } = useForm<Partial<HubSpotContact>>({
    defaultValues: {
      ...contact,
    },
  });

  const onSubmit = (data: Partial<HubSpotContact>) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        BillingRoles.canEditContacts.value
      ) ||
      (isAbleToSeeUIElement(
        changeLogRoles,
        BillingRoles.canChangePrimaryContact.value
      ) &&
        data.properties!.primary_billing_contact_ !==
          contact.properties!.primary_billing_contact_)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<HubSpotContact>) => {
      if (matchBillingAddress) {
        data.properties!.street_address_1__shipping_ = data.properties!.address;
        data.properties!.street_address_2__shipping_ =
          data.properties!.street_address_2;
        data.properties!.city__shipping_ = data.properties!.city;
        data.properties!.state__shipping_ = data.properties!.state;
        data.properties!.postal_code__shipping_ = data.properties!.zip;
        data.properties!.country__shipping_ = data.properties!.country;
      }

      return updateHubSpotContact(data, changeLogMessage);
    },
    onSuccess: (returnedContact) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Billing contact updated successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [BillingKeys.HubSpotContacts, hubSpotCompany.id],
      });

      queryClient.invalidateQueries({
        queryKey: [BillingKeys.CustomerBillingInformation],
      });

      reset(returnedContact);

      setChangeLogMessage('');
      setOpenChangeLogDialog(false);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the billing contact. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.billing_contacts.update_billing_contact, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.billingContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const canDeleteContact = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canRemoveContacts.value
  );

  const canEditContacts = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canEditContacts.value
  );

  const isPrimaryContact =
    billingInformationModel.customerInfoModel?.customerEmail ===
    contact.properties.email;

  return (
    <Accordion
      key={contact.id}
      expanded={expanded === contact.id.toString()}
      onChange={handleChange(contact.id.toString())}
      disableGutters
    >
      <AccordionSummary expandIcon={<ExpandMore />} component="div">
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%' }}
        >
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <UserAvatar
              userEmail={contact.properties.email}
              sx={{ width: 36, height: 36 }}
            />
            <Stack>
              <Typography variant="body1">
                {contact.properties.firstName} {contact.properties.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {contact.properties.email}
              </Typography>
            </Stack>
          </Stack>

          <Stack flexDirection="row" alignItems="center" gap={1} sx={{ mr: 2 }}>
            {isPrimaryContact && (
              <Tooltip title="This contact is the primary billing contact for your Google Workspace subscription. This contact will be used for any additional licenses and will be the person Promevo will contact with any payment-related questions.">
                <Chip label="primary" size="small" color="primary" />
              </Tooltip>
            )}

            <Tooltip
              title={
                isPrimaryContact
                  ? `You can't delete the primary billing contact. Please update the primary billing contact to another contact before deleting this contact.`
                  : !canDeleteContact
                  ? Messages.Errors.Permission.Acting(
                      BillingRoles.canRemoveContacts.name
                    )
                  : 'Delete'
              }
            >
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDeleteDialog(true);
                  }}
                  disabled={!canDeleteContact || isPrimaryContact}
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <ContactInfoForm
          control={control}
          errors={errors}
          billingRoles={billingRoles}
          formType="edit"
          isPrimaryContact={isPrimaryContact}
          hubSpotCompanyId={hubSpotCompany.id}
          hubSpotContacts={hubSpotContacts}
          trigger={trigger}
          watch={watch}
        />

        <Divider sx={{ my: 2 }} />

        <BillingAddressForm
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
          billingRoles={billingRoles}
          formType="edit"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={matchBillingAddress}
              onClick={() => {
                if (matchBillingAddress) {
                  setValue('properties.street_address_1__shipping_', '', {
                    shouldDirty: true,
                  });
                  setValue('properties.street_address_2__shipping_', '', {
                    shouldDirty: true,
                  });
                  setValue('properties.city__shipping_', '', {
                    shouldDirty: true,
                  });
                  setValue('properties.state__shipping_', '', {
                    shouldDirty: true,
                  });
                  setValue('properties.postal_code__shipping_', '', {
                    shouldDirty: true,
                  });
                  setValue('properties.country__shipping_', '', {
                    shouldDirty: true,
                  });

                  setMatchBillingAddress(false);
                } else {
                  setValue(
                    'properties.street_address_1__shipping_',
                    getValues('properties.address'),
                    {
                      shouldDirty: true,
                    }
                  );
                  setValue(
                    'properties.street_address_2__shipping_',
                    getValues('properties.street_address_2'),
                    {
                      shouldDirty: true,
                    }
                  );
                  setValue(
                    'properties.city__shipping_',
                    getValues('properties.city'),
                    {
                      shouldDirty: true,
                    }
                  );
                  setValue(
                    'properties.state__shipping_',
                    getValues('properties.state'),
                    {
                      shouldDirty: true,
                    }
                  );
                  setValue(
                    'properties.postal_code__shipping_',
                    getValues('properties.zip'),
                    {
                      shouldDirty: true,
                    }
                  );
                  setValue(
                    'properties.country__shipping_',
                    getValues('properties.country'),
                    {
                      shouldDirty: true,
                    }
                  );
                }

                setMatchBillingAddress(!matchBillingAddress);
              }}
              disabled={!canEditContacts}
            />
          }
          label="Shipping address matches billing address"
        />

        {!matchBillingAddress && (
          <>
            <Divider sx={{ my: 2 }} />
            <ShippingAddressForm
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
              billingRoles={billingRoles}
              formType="edit"
            />
          </>
        )}
      </AccordionDetails>
      <AccordionActions>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          loading={isPending}
          disabled={
            !isDirty ||
            (errors && errors.properties?.email?.type === 'validate')
          }
          type="submit"
          endIcon={<Save />}
        >
          Save
        </Button>
      </AccordionActions>
      {openChangeLogDialog && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            mutate(getValues());
          }}
          mutateLoading={isPending}
        />
      )}
      {openDeleteDialog && (
        <DeletePaymentContactDialog
          open={openDeleteDialog}
          handleClose={() => setOpenDeleteDialog(false)}
          selectedContact={contact}
          hubSpotCompanyId={hubSpotCompany.id}
          changeLogRoles={changeLogRoles}
        />
      )}
    </Accordion>
  );
}
